import React, { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useFormContext } from 'react-hook-form';
import { Column, Row } from 'components/shared/Layout';
import TextInput from 'components/ui/TextInput';
import { StyledRow, ErrorMessage, StyledLabel, StyledTitle } from 'pages/CreateVoucher/CreateVoucher.styles';
import { GET_BUSINESS_ACCOUNT } from 'store/queries';
import Button, { CancelButton } from 'components/ui/Button';
import {
  StyledAccountDetails,
  StyledCell,
  StyledRow as StyledRowAccount,
  StyledLabel as StyledLabelAccount
} from 'components/ui/Payments/SplitPaymentMethods/BusinessAccountPayment/BusinessAccountPayment.styles';

export const validAccountNumber = (number) => /^(\w|\d){6}$/.test(number);
export const validPostcode = (postcode) => /^(\w|\d){5,7}$/.test(postcode.replace(/\s/g, ''));

const INITIAL_STATE = {
  isActive: false,
  accountNumber: '',
  companyName: '',
  formattedAddress: '',
  phoneNumber: '',
  status: ''
};

const CheckBusinessAccount = ({ defaultValues = {}, onHandleBusinessAccount }) => {
  const [accountData, setAccountData] = useState(INITIAL_STATE);
  const { register, getValues, formState } = useFormContext();
  const { errors, touchedFields } = formState;
  const disableCheckBusinessAccount = !touchedFields?.accountNumber || !touchedFields?.postcode || errors.accountNumber || errors.postcode;

  const [fetchBusinessAccount, { loading: businessAccountLoading }] = useLazyQuery(GET_BUSINESS_ACCOUNT, {
    fetchPolicy: 'no-cache',
    onCompleted: ({ businessAccount }) => onBusinessAccountCompleted({ businessAccount }),
    onError: () => {
      onHandleBusinessAccount({ isActive: false, error: ` Invalid Account code` });
    }
  });

  const handleCheckBusinessAccount = () => {
    fetchBusinessAccount({
      variables: {
        accountNumber: getValues().accountNumber,
        postcode: getValues().postcode
      }
    });
  };

  const onBusinessAccountCompleted = ({ businessAccount }) => {
    setAccountData({
      ...INITIAL_STATE,
      isActive: businessAccount.active,
      accountNumber: businessAccount.accountNumber,
      companyName: businessAccount.companyName,
      formattedAddress: businessAccount.formattedAddress,
      phoneNumber: businessAccount.phoneNumber,
      status: businessAccount.status
    });

    return onHandleBusinessAccount({ isActive: businessAccount.active });
  };

  const loading = businessAccountLoading;

  return (
    <>
      <StyledTitle>
        <FormattedMessage id="baf.title" />
      </StyledTitle>
      <StyledRow columns={12}>
        <Row columns={12}>
          <Column alignItems="flex-start" flexDirection="column" span={3}>
            <StyledLabel htmlFor="accountNumber">
              <FormattedMessage id="baf.bafCode" />
            </StyledLabel>
            <TextInput
              data-cy="accountNumber"
              data-testid="accountNumber"
              type="text"
              width="100%"
              {...register('accountNumber', {
                required: true,
                validate: validAccountNumber
              })}
              id="accountNumber"
              placeholder="AB1234"
              defaultValue={defaultValues.accountNumber}
            />
            {errors.accountNumber && (
              <ErrorMessage>
                <FormattedMessage id="baf.invalidAccountNumber" />
              </ErrorMessage>
            )}
          </Column>

          <Column alignItems="flex-start" flexDirection="column" span={3} start={5}>
            <StyledLabel htmlFor="postcode">
              <FormattedMessage id="baf.enterPostcode" />
            </StyledLabel>
            <TextInput
              data-cy="postcode"
              data-testid="postcode"
              type="text"
              width="100%"
              {...register('postcode', {
                required: true,
                validate: validPostcode
              })}
              id="postcode"
              defaultValue={defaultValues.postcode}
            />
            {errors.postcode && (
              <ErrorMessage>
                <FormattedMessage id="baf.invalidPostcode" />
              </ErrorMessage>
            )}
          </Column>
        </Row>
      </StyledRow>
      {!accountData.accountNumber && (
        <Button
          type="button"
          data-testid="checkBaf"
          onClick={handleCheckBusinessAccount}
          loading={loading}
          disabled={loading || disableCheckBusinessAccount}
        >
          <FormattedMessage id="baf.check" />
        </Button>
      )}
      {accountData.accountNumber && (
        <Row columns={12}>
          <Column alignItems="flex-start" flexDirection="column" span={7}>
            <StyledAccountDetails>
              <>
                <StyledRowAccount>
                  <StyledLabelAccount>
                    <FormattedMessage id="baf.bafCode" />
                  </StyledLabelAccount>
                  <StyledCell data-cy="accountNumber-found">{accountData.accountNumber}</StyledCell>
                </StyledRowAccount>

                <StyledRowAccount>
                  <StyledCell>
                    <FormattedMessage id="baf.companyName" />
                  </StyledCell>
                  <StyledCell data-cy="company-found">{accountData.companyName}</StyledCell>
                </StyledRowAccount>

                <StyledRowAccount>
                  <StyledCell>
                    <FormattedMessage id="baf.formattedAddress" />
                  </StyledCell>
                  <StyledCell data-cy="address-found">{accountData.formattedAddress}</StyledCell>
                </StyledRowAccount>

                <StyledRowAccount>
                  <StyledCell>
                    <FormattedMessage id="baf.phoneNumber" />
                  </StyledCell>
                  <StyledCell data-cy="phoneNumber-found">{accountData.phoneNumber}</StyledCell>
                </StyledRowAccount>

                <StyledRowAccount>
                  <StyledCell>
                    <FormattedMessage id="baf.status" />
                  </StyledCell>
                  <StyledCell data-cy="status-found">{accountData.status}</StyledCell>
                </StyledRowAccount>
              </>
            </StyledAccountDetails>
            <CancelButton
              data-cy="baf-new"
              onClick={() => {
                setAccountData(INITIAL_STATE);
                onHandleBusinessAccount({ isActive: false });
              }}
            >
              <FormattedMessage id="baf.new" />
            </CancelButton>
          </Column>
        </Row>
      )}
    </>
  );
};

CheckBusinessAccount.propTypes = {
  onHandleBusinessAccount: PropTypes.func,
  defaultValues: PropTypes.shape({
    accountNumber: PropTypes.string,
    postcode: PropTypes.string
  })
};

export default CheckBusinessAccount;
